import dayjs, { type Dayjs } from '../../../../utils/dayjs';

export const FORM_PF_FILLING_TYPE_KEYS = {
  PERIODIC_FILLING: 'Periodic',
  CURRENT_REPORTS: 'Current'
} as const;

export type FormPFFillingTypeKey =
  (typeof FORM_PF_FILLING_TYPE_KEYS)[keyof typeof FORM_PF_FILLING_TYPE_KEYS];

interface OptionWithDescription {
  label: string;
  description: string;
  value: string;
}

export const FORM_PF_FILLING_TYPE_OPTIONS: OptionWithDescription[] = [
  {
    label: 'Periodic Filing',
    description: 'Quarterly, Annual and Transition filings',
    value: FORM_PF_FILLING_TYPE_KEYS.PERIODIC_FILLING
  },
  {
    label: 'Current Reports',
    description: 'Section 5 & 6 event reports (live from 1st July 2027)',
    value: FORM_PF_FILLING_TYPE_KEYS.CURRENT_REPORTS
  }
];

export const PERIODIC_FILLING_VERSION_KEYS = {
  LEGACY: 'v1',
  NEW: 'v2'
} as const;

export type PeriodicFillingVersionKey =
  (typeof PERIODIC_FILLING_VERSION_KEYS)[keyof typeof PERIODIC_FILLING_VERSION_KEYS];

export const PERIODIC_FILLING_VERSION_OPTIONS: OptionWithDescription[] = [
  {
    label: 'Legacy Form PF',
    description: 'For submissions until 30 June 2027',
    value: PERIODIC_FILLING_VERSION_KEYS.LEGACY
  },
  {
    label: 'New Form PF',
    description: 'For submissions from July 2027',
    value: PERIODIC_FILLING_VERSION_KEYS.NEW
  }
];

export const CURRENT_REPORTS_SECTION_KEYS = {
  SECTION_5: 'Section 5',
  SECTION_6: 'Section 6'
} as const;

export type CurrentReportsSectionKey =
  (typeof CURRENT_REPORTS_SECTION_KEYS)[keyof typeof CURRENT_REPORTS_SECTION_KEYS];

export const CURRENT_REPORTS_SECTION_OPTIONS: OptionWithDescription[] = [
  {
    label: 'Section 5',
    description: 'Event reporting for large hedge fund advisors',
    value: CURRENT_REPORTS_SECTION_KEYS.SECTION_5
  },
  {
    label: 'Section 6',
    description: 'Quarterly event reporting for private equity managers',
    value: CURRENT_REPORTS_SECTION_KEYS.SECTION_6
  }
];

export const FUND_RAUM_KEYS = {
  UNDER: 'under',
  OVER: 'over'
} as const;

export type FundRaumKey = (typeof FUND_RAUM_KEYS)[keyof typeof FUND_RAUM_KEYS];

interface FundRaumOption {
  label: string;
  value: FundRaumKey;
}

export const HEDGE_FUND_RAUM_OPTIONS: FundRaumOption[] = [
  { label: 'Under $1.5 billion', value: FUND_RAUM_KEYS.UNDER },
  { label: 'Over $1.5 billion', value: FUND_RAUM_KEYS.OVER }
];

export const LIQUIDITY_FUND_RAUM_OPTIONS: FundRaumOption[] = [
  { label: 'Under $1 billion', value: FUND_RAUM_KEYS.UNDER },
  { label: 'Over $1 billion', value: FUND_RAUM_KEYS.OVER }
];

export const FILLER_TYPE = {
  ANNUAL: 'ANNUAL',
  LARGE_HEDGE_FUND: 'LARGE_HEDGE_FUND',
  LARGE_LIQUIDITY_FUND: 'LARGE_LIQUIDITY_FUND',
  LARGE_LIQUIDITY_AND_HEDGE_FUND: 'LARGE_LIQUIDITY_AND_HEDGE_FUND'
} as const;

export type FillerType = (typeof FILLER_TYPE)[keyof typeof FILLER_TYPE];

export const FILLING_TYPE = {
  INIT: 'PF_INIT',
  UPDATING: 'PF_UPDATING',
  AMEND: 'PF_AMEND',
  TRANS: 'PF_TRANS',
  FINAL: 'PF_FINAL'
} as const;

export type FillingType = (typeof FILLING_TYPE)[keyof typeof FILLING_TYPE];

export const FILLING_TYPE_ARRAYS: OptionWithDescription[] = [
  {
    label: 'PF-INIT',
    value: FILLING_TYPE.INIT,
    description: "Select PF-INIT for the manager's first ever Form PF filing."
  },
  {
    label: 'PF-UPDATING',
    value: FILLING_TYPE.UPDATING,
    description: 'Select PF-UPDATING for each quarterly or annual Form PF filing.'
  },
  {
    label: 'PF-AMEND',
    value: FILLING_TYPE.AMEND,
    description: 'Select PF-AMEND to amend a previously submitted Form PF filing.'
  },
  {
    label: 'PF-TRANS',
    value: FILLING_TYPE.TRANS,
    description:
      'Select PF-TRANS to notify the SEC of a transition from quarterly to annual Form PF filing.'
  },
  {
    label: 'PF-FINAL',
    value: FILLING_TYPE.FINAL,
    description: "Select PF-FINAL for the manager's final Form PF filing."
  }
];

export const DATE_FORMAT = 'DD MMM YYYY';

export const FORM_PF_LEGACY_LAST_DATE = '30 Jun 2027';

export const getFillerType = (
  hedgeFundRaum: FundRaumKey,
  liquidityFundRaum: FundRaumKey
): FillerType => {
  if (hedgeFundRaum === FUND_RAUM_KEYS.OVER && liquidityFundRaum === FUND_RAUM_KEYS.OVER) {
    return FILLER_TYPE.LARGE_LIQUIDITY_AND_HEDGE_FUND;
  } else if (hedgeFundRaum === FUND_RAUM_KEYS.OVER) {
    return FILLER_TYPE.LARGE_HEDGE_FUND;
  } else if (liquidityFundRaum === FUND_RAUM_KEYS.OVER) {
    return FILLER_TYPE.LARGE_LIQUIDITY_FUND;
  } else {
    return FILLER_TYPE.ANNUAL;
  }
};

type DateLike = string | Date | Dayjs;

function roundUpToNextQuarter(date: DateLike): Dayjs {
  const d = dayjs(date);

  const quarterEnds = [
    { month: 2, day: 31 }, // March 31
    { month: 5, day: 30 }, // June 30
    { month: 8, day: 30 }, // September 30
    { month: 11, day: 31 } // December 31
  ];

  for (const q of quarterEnds) {
    const quarterEnd = dayjs(d).month(q.month).endOf('month');
    if (d.isBefore(quarterEnd)) {
      return quarterEnd;
    }
  }

  return dayjs(d).add(1, 'year').month(11).endOf('month');
}

export function adjustFiscalDate(fye: DateLike, monthsToAdd: number = 3): Dayjs {
  const target = dayjs(fye).add(monthsToAdd, 'months');

  if (target.isBefore(FORM_PF_LEGACY_LAST_DATE)) {
    return target;
  }

  return roundUpToNextQuarter(target);
}

export function getQuarter(date: DateLike): 'Q1' | 'Q2' | 'Q3' | 'Q4' {
  const d = new Date(date as string | Date);
  const month = d.getMonth();

  if (month <= 2) return 'Q1';
  if (month <= 5) return 'Q2';
  if (month <= 8) return 'Q3';
  return 'Q4';
}

export const getFormPFType = (
  submissionDueDate: string
): 'New Form PF' | 'Legacy Form PF' | null => {
  const due = dayjs(submissionDueDate, DATE_FORMAT, true);
  const legacyLast = dayjs(FORM_PF_LEGACY_LAST_DATE, DATE_FORMAT, true);

  if (!due.isValid()) return null;

  return due.isAfter(legacyLast) ? 'New Form PF' : 'Legacy Form PF';
};

const quarterList = ['Q4', 'Q1', 'Q2', 'Q3', 'Q4'] as const;

export interface FilerRow {
  id: number;
  end_date: string;
  due_date: string;
  type: string | null;
  period: string;
}

export const getAnnualFilerData = (fiscalYearEnd: DateLike): FilerRow[] => {
  const _array = Array(3).fill(null);

  return _array.map((_, index) => {
    const periodEndDate = dayjs(fiscalYearEnd).add(index, 'years').format(DATE_FORMAT);
    const submissionDueDate = dayjs(periodEndDate).add(120, 'days').format(DATE_FORMAT);

    const formPFType = getFormPFType(submissionDueDate);

    return {
      id: index + 1,
      end_date: periodEndDate,
      due_date: submissionDueDate,
      type: formPFType,
      period: 'Annual'
    };
  });
};

export const getHedgeFundQuarterlyFilerData = (fiscalYearEnd: DateLike): FilerRow[] => {
  const _array = Array(5).fill(null);

  return _array.map((_, index) => {
    const periodEndDate = adjustFiscalDate(fiscalYearEnd, index * 3).format(DATE_FORMAT);
    const submissionDueDate = dayjs(periodEndDate).add(60, 'days').format(DATE_FORMAT);
    const formPFType = getFormPFType(submissionDueDate);

    return {
      id: index + 1,
      end_date: periodEndDate,
      due_date: submissionDueDate,
      type: formPFType,
      period: quarterList[index]
    };
  });
};

export const getLiquidityFundQuarterlyFilerData = (fiscalYearEnd: DateLike): FilerRow[] => {
  const _array = Array(5).fill(null);

  return _array.map((_, index) => {
    const periodEndDate = adjustFiscalDate(fiscalYearEnd, index * 3).format(DATE_FORMAT);
    const submissionDueDate = dayjs(periodEndDate).add(15, 'days').format(DATE_FORMAT);
    const formPFType = getFormPFType(submissionDueDate);

    return {
      id: index + 1,
      end_date: periodEndDate,
      due_date: submissionDueDate,
      type: formPFType,
      period: quarterList[index]
    };
  });
};

export const getLiquidityAndHedgeFundQuarterlyFilerData = (fiscalYearEnd: DateLike): FilerRow[] => {
  const _array = Array(5).fill(null);

  return _array.map((_, index) => {
    const periodEndDate = adjustFiscalDate(fiscalYearEnd, index * 3).format(DATE_FORMAT);

    const liquiditySubmissionDueDate = dayjs(periodEndDate).add(15, 'days').format(DATE_FORMAT);
    const liquidityFormPFType = getFormPFType(liquiditySubmissionDueDate);

    const hedgeSubmissionDueDate = dayjs(periodEndDate).add(60, 'days').format(DATE_FORMAT);
    const hedgeFormPFType = getFormPFType(hedgeSubmissionDueDate);

    return {
      id: index + 1,
      end_date: periodEndDate,
      due_date: `${liquiditySubmissionDueDate} / ${hedgeSubmissionDueDate}`,
      type: `${liquidityFormPFType} / ${hedgeFormPFType}`,
      period: getQuarter(periodEndDate)
    };
  });
};

export const calculateFilerData = (
  fiscalYearEnd: DateLike,
  hedgeFundRaum: FundRaumKey,
  liquidityFundRaum: FundRaumKey
): FilerRow[] => {
  const fillerType = getFillerType(hedgeFundRaum, liquidityFundRaum);

  if (fillerType === FILLER_TYPE.ANNUAL) {
    return getAnnualFilerData(fiscalYearEnd);
  } else if (fillerType === FILLER_TYPE.LARGE_HEDGE_FUND) {
    return getHedgeFundQuarterlyFilerData(fiscalYearEnd);
  } else if (fillerType === FILLER_TYPE.LARGE_LIQUIDITY_FUND) {
    return getLiquidityFundQuarterlyFilerData(fiscalYearEnd);
  }

  return getLiquidityAndHedgeFundQuarterlyFilerData(fiscalYearEnd);
};
